import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { EMBED_TO_PARENT_MESSAGES, PARENT_TO_EMBED_MESSAGES } from '../constants/embedMessageConstants';
import { useProgramSearch, SearchResult } from './useProgramSearch';
import { useEmbedParams } from './useEmbedParams';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { selectFavoritesSet, selectHasAttemptedFavoritesFetch } from '../store/selectors/userPreferencesSelectors';
import { toggleUserFavorite, optimisticToggleFavorite } from '../store/slices/userPreferencesSlice';
import { FavoriteAction } from '../types/userPreferences/userPreferences.types';
import { getFavoriteKey, getFavoriteTypeAndId } from '../utils/favoriteUtils';
import { appendEmbedParamsToPath } from '../utils/navigationUtils';
import { logger } from '../utils/logger';

const SEARCH_CATEGORIES = [
  { id: 'all', label: 'All' },
  { id: 'programs', label: 'Programs' },
  { id: 'reports', label: 'Reports' },
  { id: 'announcements', label: 'Announcements' },
  { id: 'resources', label: 'Resources' },
];

const SECTION_CATEGORY_MAP: Record<string, string> = {
  Programs: 'programs',
  Reports: 'reports',
  Announcements: 'announcements',
  Resources: 'resources',
};

/** Message types this hook handles — excludes THEME_CHANGE, NAVIGATE_HOME etc. */
const VALID_SEARCH_MESSAGE_TYPES = new Set([
  PARENT_TO_EMBED_MESSAGES.SEARCH_QUERY,
  PARENT_TO_EMBED_MESSAGES.SEARCH_RESULT_CLICK,
  PARENT_TO_EMBED_MESSAGES.SEARCH_ACTION,
]);

const postToParent = (type: string, payload?: Record<string, unknown>): void => {
  if (window.parent && window.parent !== window) {
    window.parent.postMessage({ type, payload }, '*');
  }
};

/** Build a unique result ID — includes section title and index for guaranteed uniqueness */
const buildResultId = (sectionTitle: string, r: SearchResult, index: number): string =>
  `${sectionTitle}-${r.type}-${index}-${r.displayId || r.label || ''}`;

/** Enrich raw sections with favorite state and badge metadata for proxy rendering */
const enrichSections = (sections: { title: string; results: SearchResult[] }[], favoriteIdsSet: Set<string>) =>
  sections.map((s) => ({
    ...s,
    results: s.results.map((r, i) => {
      const key = r.favoriteNode ? getFavoriteKey(r.favoriteNode) : null;
      const isFav = key ? favoriteIdsSet.has(key) : false;
      return {
        ...r,
        id: buildResultId(s.title, r, i),
        actions: {
          ...(r.favoriteNode ? { favorite: { isFavorited: isFav } } : {}),
          ...(r.externalUrl ? { externalLink: { url: r.externalUrl } } : {}),
        },
        badges: {
          ...(r.type === 'product-line' ? { typeBadge: { label: 'PL', variant: 'product-line' } } : {}),
          ...(r.type === 'rpn' ? { typeBadge: { label: 'RPN', variant: 'rpn' } } : {}),
          ...(r.type === 'fpn' ? { typeBadge: { label: 'FPN', variant: 'fpn' } } : {}),
          ...(r.type === 'rpn' && r.programStatus ? { statusBadge: { label: r.programStatus, color: 'blue' } } : {}),
          ...(r.isConfidential ? { confidential: true } : {}),
        },
      };
    }),
  }));

/**
 * When Verity is embedded in FinSuite, this hook:
 * 1. Sends SEARCH_CAPABILITIES on mount so FinSuite knows to delegate search
 * 2. Listens for SEARCH_QUERY → runs useProgramSearch → sends SEARCH_RESULTS
 * 3. Listens for SEARCH_RESULT_CLICK → navigates internally
 * 4. Listens for SEARCH_ACTION → handles favorite toggle → sends SEARCH_ACTION_RESULT
 */
export const useSearchResponder = (): void => {
  const { isEmbedded } = useEmbedParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const favoriteIdsSet = useAppSelector(selectFavoritesSet);
  const hasAttemptedFavoritesFetch = useAppSelector(selectHasAttemptedFavoritesFetch);

  const [query, setQuery] = useState('');
  const queryRef = useRef('');
  const categoryRef = useRef('all');
  const { sections, ensureDataLoaded } = useProgramSearch(query);

  // Keep refs to avoid stale closures
  const sectionsRef = useRef(sections);
  sectionsRef.current = sections;
  const favoriteIdsSetRef = useRef(favoriteIdsSet);
  favoriteIdsSetRef.current = favoriteIdsSet;
  const hasAttemptedRef = useRef(hasAttemptedFavoritesFetch);
  hasAttemptedRef.current = hasAttemptedFavoritesFetch;

  // Send capabilities on embed ready
  useEffect(() => {
    if (!isEmbedded) return;
    postToParent(EMBED_TO_PARENT_MESSAGES.SEARCH_CAPABILITIES, {
      supported: true,
      placeholder: 'Search Verity  ·  programs, reports, resources...',
      categories: SEARCH_CATEGORIES,
      variant: 'verity',
    });
    logger?.info('Sent SEARCH_CAPABILITIES to parent');
  }, [isEmbedded]);

  // Send results whenever sections change (triggered by query change)
  useEffect(() => {
    if (!isEmbedded || !queryRef.current) return;
    const cat = categoryRef.current;
    const filtered = cat && cat !== 'all' ? sections.filter((s) => SECTION_CATEGORY_MAP[s.title] === cat) : sections;
    const enriched = enrichSections(filtered, favoriteIdsSetRef.current);
    postToParent(EMBED_TO_PARENT_MESSAGES.SEARCH_RESULTS, {
      query: queryRef.current,
      sections: enriched,
    });
  }, [isEmbedded, sections]);

  const handleFavoriteToggle = useCallback(
    async (resultId: string, result: SearchResult) => {
      if (!result.favoriteNode || !hasAttemptedRef.current) return;
      const typeAndId = getFavoriteTypeAndId(result.favoriteNode);
      if (!typeAndId) return;

      const key = getFavoriteKey(result.favoriteNode);
      const isFav = key ? favoriteIdsSetRef.current.has(key) : false;
      const action = isFav ? FavoriteAction.REMOVED : FavoriteAction.ADDED;

      dispatch(
        optimisticToggleFavorite({
          favorite_type: typeAndId.favoriteType,
          item_id: typeAndId.itemId,
          isFavorited: isFav,
          action,
        })
      );

      try {
        await dispatch(
          toggleUserFavorite({ favorite_type: typeAndId.favoriteType, item_id: typeAndId.itemId, isFavorited: isFav })
        ).unwrap();
        postToParent(EMBED_TO_PARENT_MESSAGES.SEARCH_ACTION_RESULT, {
          resultId,
          success: true,
          updatedFavoriteState: !isFav,
        });
      } catch (error) {
        logger?.error('useSearchResponder: favorite toggle failed', { resultId, error });
        dispatch(
          optimisticToggleFavorite({
            favorite_type: typeAndId.favoriteType,
            item_id: typeAndId.itemId,
            isFavorited: !isFav,
            action: isFav ? FavoriteAction.ADDED : FavoriteAction.REMOVED,
          })
        );
        postToParent(EMBED_TO_PARENT_MESSAGES.SEARCH_ACTION_RESULT, { resultId, success: false });
      }
    },
    [dispatch]
  );

  // Listen for parent messages
  useEffect(() => {
    if (!isEmbedded) return;

    const handleMessage = (event: MessageEvent) => {
      const { type, payload } = event.data || {};

      // Validate message is a known parent-to-embed type
      if (!type || !VALID_SEARCH_MESSAGE_TYPES.has(type)) return;

      logger?.debug('useSearchResponder: received message', { type });

      switch (type) {
        case PARENT_TO_EMBED_MESSAGES.SEARCH_QUERY: {
          const { query: q, category } = payload || {};
          queryRef.current = q || '';
          categoryRef.current = category || 'all';
          setQuery(q || '');
          ensureDataLoaded();
          // Results will be sent via the useEffect when sections update
          break;
        }
        case PARENT_TO_EMBED_MESSAGES.SEARCH_RESULT_CLICK: {
          const { path, externalUrl } = payload || {};
          if (path) {
            navigate(appendEmbedParamsToPath(path));
          } else if (externalUrl) {
            window.open(externalUrl, '_blank', 'noopener,noreferrer');
          }
          break;
        }
        case PARENT_TO_EMBED_MESSAGES.SEARCH_ACTION: {
          const { resultId, action: actionType } = payload || {};
          if (actionType === 'toggle-favorite') {
            // Find the result in current sections using stable ID
            for (const section of sectionsRef.current) {
              const found = section.results.find((r, i) => buildResultId(section.title, r, i) === resultId);
              if (found) {
                handleFavoriteToggle(resultId, found);
                break;
              }
            }
          }
          break;
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [isEmbedded, ensureDataLoaded, navigate, handleFavoriteToggle]);
};
